import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import {
  type LucideIcon,
  ArrowRight,
  CheckCircle2,
  MapPin,
  Phone,
  ChevronRight,
  Building2,
} from "lucide-react";
import { SiteLayout } from "@/components/SiteLayout";
import { PageHero, type HeroImage } from "@/components/PageHero";
import { PricingTeaserStrip } from "@/components/PricingTeaserStrip";
import { LastUpdated } from "@/components/LastUpdated";
import { getPageLastUpdated } from "@/lib/lastUpdatedRegistry";

import { FinalCTA } from "@/components/home/HomeSections";
import { StickyCTABar } from "@/components/StickyCTABar";
import { FreeAuditCTABanner } from "@/components/FreeAuditCTABanner";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import type { ServicePricing } from "@/lib/pricingData";
import { CITY_SERVICES, type CityServiceKey } from "@/lib/cityServicesData";
import { getSiblingCityCombos, isSuppressedCombo } from "@/lib/cityServiceMatrix";
import { CITY_PAGES } from "@/lib/citiesData";
import { INDUSTRY_PAGES } from "@/lib/industriesData";
import {
  buildServiceHubServiceJsonLd,
  buildServiceHubLocalBusinessRef,
  buildServiceHubBreadcrumbJsonLd,
  buildFaqPageJsonLd,
} from "@/lib/serviceSchema";
import {
  RelatedPagesGrid,
  type RelatedPageCard,
} from "@/components/RelatedPagesGrid";
import { CITY_HUBS, INDUSTRY_HUBS } from "@/lib/hubLinkData";

export type ServiceFeature = { icon: LucideIcon; title: string; desc: string };
export type FrameworkStep = { number: string; title: string; desc: string };
export type IndustryItem = { icon: LucideIcon; name: string; desc: string };
export type FAQItem = { q: string; a: string };

export type ServicePageProps = {
  // SEO + page identity
  slug: string;
  serviceName: string; // e.g. "SEO", "Web Design"
  pricingRoute?: string; // e.g. "/seo-services/pricing"

  // Hero
  eyebrow: string;
  title: string;
  goldWord: string;
  description: string;
  /** Optional geo-tagged hero photo rendered behind the gradient overlay. */
  heroImage?: HeroImage;

  // Problem / fit
  intro: { heading: string; body: string[] };
  benefits: string[];

  // Service feature grid (what's included)
  features: ServiceFeature[];

  // Framework / how we do it
  frameworkHeading?: string;
  frameworkIntro?: string;
  framework?: FrameworkStep[];

  // Industries served
  industries?: IndustryItem[];

  // FAQs
  faqs?: FAQItem[];

  // Optional pricing tiers preview (uses ServicePricing tiers)
  pricing?: ServicePricing;

  // Optional extra sections to render at key insertion points.
  // Used by service-specific pages (e.g. web-design) to inject
  // process steps, platform info, custom mockup forms, and richer
  // package blocks without forking the whole template.
  sectionsAfterIntro?: ReactNode;
  sectionsBeforeFramework?: ReactNode;
  sectionsAfterFramework?: ReactNode;
  sectionsBeforePricing?: ReactNode;
  sectionsAfterPricing?: ReactNode;
};

/**
 * Build the keyword-rich anchor text for a city × service link.
 * Examples:
 *   seo + Colorado Springs        → "Colorado Springs SEO"
 *   web-design + Denver           → "Denver Web Design"
 */
function cityServiceAnchor(serviceKey: CityServiceKey, cityName: string): string {
  switch (serviceKey) {
    case "seo":
      return `${cityName} SEO`;
    case "web-design":
      return `${cityName} Web Design`;
    default:
      return `${cityName} services`;
  }
}

export function ServicePage(props: ServicePageProps) {
  const {
    slug,
    serviceName,
    pricingRoute,
    eyebrow,
    title,
    goldWord,
    description,
    heroImage,
    intro,
    features,
    benefits,
    frameworkHeading,
    frameworkIntro,
    framework,
    industries,
    faqs,
    pricing,
    sectionsAfterIntro,
    sectionsBeforeFramework,
    sectionsAfterFramework,
    sectionsBeforePricing,
    sectionsAfterPricing,
  } = props;

  // Derive the matching city service key from the page slug. Only services
  // that have city spoke pages (seo, web-design, google-maps) get the
  // city-by-city linking grid below; other hubs (ppc, social-media, geo-ai)
  // skip it gracefully.
  const cityServiceKey: CityServiceKey | null =
    slug in CITY_SERVICES ? (slug as CityServiceKey) : null;
  const cityCombos = cityServiceKey
    ? // Pass an unused exclude slug so we get every city back.
      getSiblingCityCombos(cityServiceKey, "__none__")
    : [];

  // Featured 5-city block — the Colorado markets we prioritize for every
  // service. These render on EVERY service page (with or without dedicated
  // city spoke pages), linking to the most specific landing page available:
  // /{service}/{city} when a spoke exists, otherwise /service-areas/{city}.
  const FEATURED_CITY_SLUGS = [
    "denver",
    "colorado-springs",
    "fort-collins",
    "castle-rock",
    "pueblo",
  ] as const;
  const FEATURED_CITY_BLURBS: Record<string, string> = {
    denver: "Win in Colorado's most competitive metro — LoDo, RiNo, Cherry Creek, Highlands.",
    "colorado-springs": "Capture high-intent searches across Briargate, Powers, and the broader El Paso County footprint.",
    "fort-collins": "Northern Colorado coverage — Fort Collins, Loveland, Windsor, and Wellington.",
    "castle-rock": "Premium positioning across The Meadows, Founders Village, and Castle Pines.",
    pueblo: "Hometown trust signals built for Pueblo and Pueblo West buyers.",
  };
  const featuredCities = FEATURED_CITY_SLUGS
    .map((s) => CITY_PAGES.find((c) => c.slug === s))
    .filter((c): c is (typeof CITY_PAGES)[number] => Boolean(c))
    // Drop any city whose spoke for this service is suppressed (e.g. Denver +
    // local-seo) so the featured grid never links to a suppressed combo.
    .filter((c) => !cityServiceKey || !isSuppressedCombo(c.slug, cityServiceKey));

  const titleParts = title.split(goldWord);

  // JSON-LD: Service node references the canonical Organization /
  // LocalBusiness entities defined in __root.tsx via @id, so search engines
  // attach this Service to the same real-world business. We also emit
  // a per-page LocalBusiness reference (with makesOffer pointing to this
  // Service), a BreadcrumbList, and a FAQPage when FAQs are provided.
  const serviceJsonLd = buildServiceHubServiceJsonLd({
    slug,
    serviceName,
    description,
    pricing,
  });
  const localBusinessJsonLd = buildServiceHubLocalBusinessRef({
    slug,
    serviceName,
  });
  const breadcrumbJsonLd = buildServiceHubBreadcrumbJsonLd({
    slug,
    serviceName,
  });
  const faqJsonLd = faqs && faqs.length ? buildFaqPageJsonLd(faqs) : null;

  return (
    <SiteLayout>
      {/* JSON-LD */}
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceJsonLd) }}
      />
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: JSON.stringify(localBusinessJsonLd) }}
      />
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }}
      />
      {faqJsonLd && (
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
        />
      )}

      <PageHero
        eyebrow={eyebrow}
        title={
          <>
            {titleParts[0]}
            <span className="text-gradient-gold">{goldWord}</span>
            {titleParts.slice(1).join(goldWord)}
          </>
        }
        description={description}
        image={heroImage}
      >
        {/* Breadcrumbs */}
        <nav
          aria-label="Breadcrumb"
          className="mx-auto mb-6 flex items-center justify-center gap-1.5 text-xs font-medium text-brand-cream/85"
        >
          <Link to="/" className="hover:text-brand-gold">
            Home
          </Link>
          <ChevronRight className="h-3 w-3" />
          <span className="text-brand-gold">{serviceName}</span>
        </nav>

        <div className="flex flex-col items-center justify-center gap-3 sm:flex-row">
          <Link
            to="/contact"
            className="group inline-flex items-center justify-center gap-2 rounded-md bg-gradient-gold px-7 py-4 text-base font-bold text-brand-black shadow-gold transition-spring hover:-translate-y-0.5"
          >
            Get a Free Proposal
            <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
          </Link>
          <a
            href="tel:+17194193935"
            className="inline-flex items-center justify-center gap-2 rounded-md border border-white/30 bg-white/5 px-7 py-4 text-base font-semibold text-white backdrop-blur transition-smooth hover:bg-white/10"
           aria-label="Call (719) 419-3935">
            <Phone className="h-4 w-4" /> (719) 419-3935
          </a>
        </div>

        {/* Quick differentiators */}
        <div className="mx-auto mt-8 grid max-w-3xl grid-cols-1 gap-2 text-sm font-semibold text-brand-cream/85 sm:grid-cols-3">
          {["Colorado-only agency", "Home-service specialists", "Direct owner access"].map((d) => (
            <div
              key={d}
              className="flex items-center justify-center gap-2 rounded-md border border-white/10 bg-white/5 px-3 py-2"
            >
              <CheckCircle2 className="h-4 w-4 text-brand-gold" />
              {d}
            </div>
          ))}
        </div>
      </PageHero>

      <PricingTeaserStrip />


      {/* Freshness signal — visible "Last updated" date for users + crawlers */}
      <div className="border-b border-border bg-muted/30 py-4">
        <div className="mx-auto max-w-6xl px-5 lg:px-8">
          <LastUpdated date={getPageLastUpdated(`/${slug}`)} />
        </div>
      </div>

      {/* Problem / Fit + benefits */}
      <section className="bg-background py-20 lg:py-28">
        <div className="mx-auto grid max-w-6xl gap-12 px-5 lg:grid-cols-2 lg:px-8">
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand-gold-deep">
              Why It Matters in Colorado
            </span>
            <h2 className="mt-3 text-4xl font-extrabold leading-tight text-foreground">
              {intro.heading}
            </h2>
            <div className="mt-6 space-y-4 text-lg leading-relaxed text-muted-foreground">
              {intro.body.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </div>
          <ul className="space-y-4">
            {benefits.map((b) => (
              <li
                key={b}
                className="flex items-start gap-4 rounded-2xl border border-border bg-card p-5 shadow-card"
              >
                <CheckCircle2 className="mt-0.5 h-6 w-6 shrink-0 text-brand-gold-deep" />
                <span className="text-base font-medium text-foreground">{b}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {sectionsAfterIntro}

      {/* What's Included */}
      <section className="bg-section-alt py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <span className="inline-block rounded-full border border-brand-gold-deep/30 bg-brand-gold/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-brand-gold-deep">
              What's Included
            </span>
            <h2 className="mt-5 text-4xl font-extrabold text-foreground sm:text-5xl">
              Our <span className="text-gradient-gold">{serviceName}</span> stack
            </h2>
          </div>

          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {features.map((f) => (
              <div
                key={f.title}
                className="rounded-2xl border border-border bg-card p-7 shadow-card transition-spring hover:-translate-y-1 hover:border-brand-gold/50 hover:shadow-card-hover"
              >
                <div className="grid h-12 w-12 place-items-center rounded-xl bg-secondary text-brand-gold">
                  <f.icon className="h-6 w-6" />
                </div>
                <h3 className="mt-5 text-xl font-bold text-foreground">{f.title}</h3>
                <p className="mt-2 leading-relaxed text-muted-foreground">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {sectionsBeforeFramework}

      {/* Framework — how we do it */}
      {framework && framework.length > 0 && (
        <section className="bg-background py-20 lg:py-28">
          <div className="mx-auto max-w-6xl px-5 lg:px-8">
            <div className="mx-auto max-w-3xl text-center">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand-gold-deep">
                Our Framework
              </span>
              <h2 className="mt-3 text-4xl font-extrabold text-foreground sm:text-5xl">
                {frameworkHeading ?? `The Colorado ${serviceName} Method`}
              </h2>
              {frameworkIntro && (
                <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
                  {frameworkIntro}
                </p>
              )}
            </div>

            <ol className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {framework.map((step) => (
                <li
                  key={step.title}
                  className="relative rounded-2xl border border-border bg-card p-7 shadow-card"
                >
                  <div className="text-5xl font-black leading-none text-brand-gold/40">
                    {step.number}
                  </div>
                  <h3 className="mt-4 text-xl font-bold text-foreground">{step.title}</h3>
                  <p className="mt-2 leading-relaxed text-muted-foreground">{step.desc}</p>
                </li>
              ))}
            </ol>

            {/* Mid-page CTA */}
            <div className="mt-14 rounded-2xl border border-brand-gold/30 bg-gradient-to-r from-brand-gold/10 via-brand-gold/5 to-transparent p-6 sm:flex sm:items-center sm:justify-between sm:p-8">
              <div>
                <h3 className="text-xl font-extrabold text-foreground">
                  Want this framework applied to your Colorado business?
                </h3>
                <p className="mt-1 text-muted-foreground">
                  Free strategy session — no pressure, just real recommendations.
                </p>
              </div>
              <Link
                to="/contact"
                className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-md bg-gradient-gold px-6 py-3 text-sm font-bold text-brand-black shadow-gold transition-spring hover:-translate-y-0.5 sm:mt-0 sm:w-auto"
              >
                Request {serviceName} audit
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {sectionsAfterFramework}

      {sectionsBeforePricing}

      {sectionsAfterPricing}

      {/* Industries served */}
      {industries && industries.length > 0 && (
        <section className="bg-background py-20 lg:py-28">
          <div className="mx-auto max-w-7xl px-5 lg:px-8">
            <div className="mx-auto max-w-3xl text-center">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand-gold-deep">
                Who We Help
              </span>
              <h2 className="mt-3 text-4xl font-extrabold text-foreground sm:text-5xl">
                {serviceName} for these <span className="text-gradient-gold">Colorado industries</span>
              </h2>
            </div>

            <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {industries.map((ind) => (
                <div
                  key={ind.name}
                  className="rounded-2xl border border-border bg-card p-6 shadow-card transition-spring hover:-translate-y-1 hover:border-brand-gold/50"
                >
                  <div className="flex items-center gap-3">
                    <div className="grid h-11 w-11 place-items-center rounded-xl bg-secondary text-brand-gold">
                      <ind.icon className="h-5 w-5" />
                    </div>
                    <h3 className="text-lg font-bold text-foreground">{ind.name}</h3>
                  </div>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{ind.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Featured Colorado Cities — focused 5-city block (Denver, Colorado
          Springs, Fort Collins, Castle Rock, Pueblo). Renders on EVERY
          service page. Each card links to the most specific landing page
          available for this service: /{service}/{city} when a spoke page
          exists, otherwise /service-areas/{city}. */}
      <section className="bg-background py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-brand-gold-deep">
              <MapPin className="h-4 w-4" /> Colorado Cities We Serve
            </span>
            <h2 className="mt-3 text-4xl font-extrabold text-foreground sm:text-5xl">
              {serviceName} in{" "}
              <span className="text-gradient-gold">Colorado's top markets</span>
            </h2>
            <p className="mt-4 text-lg text-muted-foreground">
              Local {serviceName.toLowerCase()} for the five Colorado cities
              that drive the bulk of our work — each with its own dedicated
              landing page, written for the way buyers in that market actually
              search.
            </p>
          </div>

          <ul className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
            {featuredCities.map((city) => {
              const anchor = cityServiceKey
                ? cityServiceAnchor(cityServiceKey, city.name)
                : `${city.name} ${serviceName}`;
              const blurb = FEATURED_CITY_BLURBS[city.slug];
              const cardClass =
                "group flex h-full flex-col rounded-2xl border border-border bg-card p-6 shadow-card transition-spring hover:-translate-y-1 hover:border-brand-gold/60 hover:shadow-card-hover";
              const cardInner = (
                <>
                  <div className="grid h-11 w-11 place-items-center rounded-xl bg-gradient-gold text-brand-black shadow-gold">
                    <MapPin className="h-5 w-5" />
                  </div>
                  <h3 className="mt-5 text-lg font-extrabold text-foreground">
                    {anchor}
                  </h3>
                  <p className="mt-1 text-xs font-semibold uppercase tracking-wider text-brand-gold-deep">
                    {city.region}
                  </p>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-muted-foreground">
                    {blurb}
                  </p>
                  <span className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-foreground group-hover:text-brand-gold-deep">
                    Explore {city.name}
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </>
              );
              return (
                <li key={city.slug}>
                  {cityServiceKey === "seo" && (
                    <a href={`/${city.slug}-seo`} className={cardClass}>
                      {cardInner}
                    </a>
                  )}
                  {cityServiceKey === "web-design" && (
                    <a href={`/${city.slug}-web-design`} className={cardClass}>
                      {cardInner}
                    </a>
                  )}
                  {/* For service hubs without a dedicated city spoke (local-seo,
                      ppc, geo-ai, etc.), fall back to the city hub which lists
                      all live spokes for that city. */}
                  {cityServiceKey !== "seo" && cityServiceKey !== "web-design" && (
                    <Link to="/service-areas/$citySlug" params={{ citySlug: city.slug }} className={cardClass}>
                      {cardInner}
                    </Link>
                  )}
                </li>
              );
            })}
          </ul>

          <div className="mt-10 text-center">
            <Link
              to="/service-areas"
              className="inline-flex items-center gap-2 text-sm font-bold text-brand-gold-deep hover:underline"
            >
              See all Colorado service areas
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* {Service} by Colorado city — links to every city spoke page.
          Only renders for services that have city pages (seo, web-design,
          google-maps). Each link uses keyword-rich anchor text like
          "Colorado Springs SEO" → /colorado-springs-seo. */}
      {cityServiceKey && cityCombos.length > 0 && (
        <section className="bg-section-alt py-20 lg:py-28">
          <div className="mx-auto max-w-6xl px-5 lg:px-8">
            <div className="mx-auto max-w-3xl text-center">
              <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-brand-gold-deep">
                <MapPin className="h-4 w-4" /> Colorado Coverage
              </span>
              <h2 className="mt-3 text-4xl font-extrabold text-foreground sm:text-5xl">
                {serviceName} by{" "}
                <span className="text-gradient-gold">Colorado city</span>
              </h2>
              <p className="mt-4 text-lg text-muted-foreground">
                Dedicated {serviceName.toLowerCase()} pages for every Colorado
                market we serve — written for the way buyers in each city
                actually search.
              </p>
            </div>

            <ul className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {cityCombos.map((combo) => {
                const anchor = cityServiceAnchor(cityServiceKey, combo.city.name);
                return (
                  <li key={combo.path}>
                    <a
                      href={combo.path}
                      className="group flex items-start gap-3 rounded-xl border border-border bg-card p-4 shadow-card transition-spring hover:-translate-y-0.5 hover:border-brand-gold/50 hover:shadow-card-hover"
                    >
                      <div className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-secondary text-brand-gold">
                        <MapPin className="h-4 w-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-sm font-bold text-foreground">
                            {anchor}
                          </span>
                          <ArrowRight className="h-4 w-4 shrink-0 text-brand-gold-deep transition-transform group-hover:translate-x-1" />
                        </div>
                        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                          {combo.city.region} · serving{" "}
                          {combo.city.neighborhoods.slice(0, 2).join(", ")}
                        </p>
                      </div>
                    </a>
                  </li>
                );
              })}
            </ul>

            <div className="mt-8 text-center">
              <Link
                to="/service-areas"
                className="inline-flex items-center gap-2 text-sm font-bold text-brand-gold-deep hover:underline"
              >
                See all Colorado service areas
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Fallback Colorado coverage grid for hubs without dedicated city
          spoke pages (PPC, social media, GEO/AI). Links each city to its
          /service-areas/{slug} hub with keyword-rich anchor text like
          "Colorado Springs PPC" so the parent service hub still aggregates
          internal links to every Colorado city we serve. */}
      {!cityServiceKey && (
        <section className="bg-section-alt py-20 lg:py-28">
          <div className="mx-auto max-w-6xl px-5 lg:px-8">
            <div className="mx-auto max-w-3xl text-center">
              <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-brand-gold-deep">
                <MapPin className="h-4 w-4" /> Colorado Coverage
              </span>
              <h2 className="mt-3 text-4xl font-extrabold text-foreground sm:text-5xl">
                {serviceName} for{" "}
                <span className="text-gradient-gold">Colorado cities</span>
              </h2>
              <p className="mt-4 text-lg text-muted-foreground">
                We deliver {serviceName.toLowerCase()} for businesses across
                every priority Colorado market. Click your city for the full
                local services overview.
              </p>
            </div>

            <ul className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {CITY_PAGES.map((city) => (
                <li key={city.slug}>
                  <Link
                    to="/service-areas/$citySlug"
                    params={{ citySlug: city.slug }}
                    className="group flex items-start gap-3 rounded-xl border border-border bg-card p-4 shadow-card transition-spring hover:-translate-y-0.5 hover:border-brand-gold/50 hover:shadow-card-hover"
                  >
                    <div className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-secondary text-brand-gold">
                      <MapPin className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-bold text-foreground">
                          {city.name} {serviceName}
                        </span>
                        <ArrowRight className="h-4 w-4 shrink-0 text-brand-gold-deep transition-transform group-hover:translate-x-1" />
                      </div>
                      <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                        {city.region} · serving{" "}
                        {city.neighborhoods.slice(0, 2).join(", ")}
                      </p>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>

            <div className="mt-8 text-center">
              <Link
                to="/service-areas"
                className="inline-flex items-center gap-2 text-sm font-bold text-brand-gold-deep hover:underline"
              >
                See all Colorado service areas
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* {Service} by industry — links to every industry spoke page.
          Renders for every service hub. Anchor text leads with the
          industry name so each link is keyword-rich for that vertical. */}
      {INDUSTRY_PAGES.length > 0 && (
        <section className="bg-background py-20 lg:py-28">
          <div className="mx-auto max-w-6xl px-5 lg:px-8">
            <div className="mx-auto max-w-3xl text-center">
              <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-brand-gold-deep">
                <Building2 className="h-4 w-4" /> By Industry
              </span>
              <h2 className="mt-3 text-4xl font-extrabold text-foreground sm:text-5xl">
                {serviceName} by{" "}
                <span className="text-gradient-gold">Colorado industry</span>
              </h2>
              <p className="mt-4 text-lg text-muted-foreground">
                Industry-specific pages with examples, common keywords, and the
                tactics that actually move the needle for each vertical in
                Colorado.
              </p>
            </div>

            <ul className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {INDUSTRY_PAGES.map((ind) => (
                <li key={ind.slug}>
                  <Link
                    to="/industries/$industrySlug"
                    params={{ industrySlug: ind.slug }}
                    className="group flex items-start gap-3 rounded-xl border border-border bg-card p-4 shadow-card transition-spring hover:-translate-y-0.5 hover:border-brand-gold/50 hover:shadow-card-hover"
                  >
                    <div className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-secondary text-brand-gold">
                      <Building2 className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-bold text-foreground">
                          {ind.name} {serviceName} in Colorado
                        </span>
                        <ArrowRight className="h-4 w-4 shrink-0 text-brand-gold-deep transition-transform group-hover:translate-x-1" />
                      </div>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>

            <div className="mt-8 text-center">
              <Link
                to="/industries"
                className="inline-flex items-center gap-2 text-sm font-bold text-brand-gold-deep hover:underline"
              >
                See all Colorado industries we serve
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>
      )}

      <FreeAuditCTABanner
        headline={`Get your free Colorado ${serviceName.toLowerCase()} audit`}
      />

      {/* FAQs */}
      {faqs && faqs.length > 0 && (
        <section className="bg-background py-20 lg:py-28">
          <div className="mx-auto max-w-3xl px-5 lg:px-8">
            <div className="text-center">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand-gold-deep">
                FAQs
              </span>
              <h2 className="mt-3 text-4xl font-extrabold text-foreground sm:text-5xl">
                Colorado {serviceName} <span className="text-gradient-gold">questions</span>
              </h2>
            </div>

            <Accordion type="single" collapsible className="mt-10">
              {faqs.map((f, i) => (
                <AccordionItem key={f.q} value={`faq-${i}`}>
                  <AccordionTrigger className="text-left text-base font-bold">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-base leading-relaxed text-muted-foreground">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>
      )}

      <ServiceHubCrossLinks serviceName={serviceName} />

      <FinalCTA />
      <StickyCTABar />
    </SiteLayout>
  );
}

/**
 * Cross-hub links for every Service hub — satisfies Rule #4 (every hub links
 * to the other two hub types). Uses varied anchor text per Rule #5 by
 * combining the city/industry hub label with the current service name.
 */
function ServiceHubCrossLinks({ serviceName }: { serviceName: string }) {
  const cityCards: RelatedPageCard[] = [
    CITY_HUBS.coloradoSprings,
    CITY_HUBS.denver,
    CITY_HUBS.fortCollins,
    CITY_HUBS.boulder,
    CITY_HUBS.pueblo,
    CITY_HUBS.aurora,
    CITY_HUBS.greeley,
    CITY_HUBS.grandJunction,
  ].map((c) => ({
    to: c.to,
    title: `${serviceName} for ${c.title.replace(" digital marketing agency", "").replace(" digital marketing", "")} businesses`,
    description: c.description,
  }));

  const industryCards: RelatedPageCard[] = [
    INDUSTRY_HUBS.hvac,
    INDUSTRY_HUBS.plumbing,
    INDUSTRY_HUBS.roofing,
    INDUSTRY_HUBS.electrical,
    INDUSTRY_HUBS.landscaping,
    INDUSTRY_HUBS.contractor,
    INDUSTRY_HUBS.lawFirm,
    INDUSTRY_HUBS.dental,
  ].map((i) => ({
    to: i.to,
    title: `${serviceName} for ${i.title.replace(" marketing Colorado", "").replace(" SEO Colorado", "")} companies`,
    description: i.description,
  }));

  return (
    <RelatedPagesGrid
      heading="More from Colorado Web Impressions"
      intro={`Where ${serviceName} fits in our flat hub-and-spoke model — explore Colorado cities and industries we specialize in.`}
      cards={[...cityCards, ...industryCards]}
    />
  );
}
